import React, { useEffect, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  FileDown, Play, Save, Settings2, BookOpen, Eye, CalendarClock,
} from 'lucide-react';

import { useReportBuilder } from '@/hooks/useReportBuilder';
import { DatasetSelector } from './DatasetSelector';
import { ColumnPicker } from './ColumnPicker';
import { FilterPanel } from './FilterPanel';
import { SortPanel } from './SortPanel';
import { PreviewTable } from './PreviewTable';
import { SaveTemplateDialog } from './SaveTemplateDialog';
import { TemplatesList } from './TemplatesList';
import { ScheduleTab } from './ScheduleTab';
import { PortfolioScopeSelector } from './PortfolioScopeSelector';

interface Props {
  open: boolean;
  onClose: () => void;
  contratoId: string;
  contratoNome?: string;
}

export const ReportBuilderDrawer: React.FC<Props> = ({ open, onClose, contratoId, contratoNome }) => {
  const {
    // scope
    escopo, portfolioClusterId, portfolioContratoIds, isGestorOrAbove, portfolioReady,
    setEscopo, setPortfolioClusterId, setPortfolioContratoIds,
    // report
    dataset, config, previewRows, previewLoading, previewError,
    templates, templatesLoading, availableDatasets, currentDataset,
    setDataset, toggleColumn, addFilter, updateFilter, removeFilter, toggleSort,
    schedulePreview, saveTemplate, deleteTemplate, toggleFavorito, loadTemplate, exportData,
  } = useReportBuilder(contratoId);

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadedTemplateId, setLoadedTemplateId] = useState<string | null>(null);

  useEffect(() => {
    if (open) schedulePreview(config, dataset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, dataset, open]);

  const canExport = config.columns.length > 0 && (escopo === 'contrato' || portfolioReady);

  return (
    <>
      <Drawer open={open} onClose={onClose} direction="right">
        <DrawerContent className="fixed right-0 top-0 h-full w-full max-w-3xl flex flex-col rounded-l-2xl rounded-r-none overflow-hidden">
          <DrawerHeader className="px-6 py-4 border-b shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Settings2 className="h-5 w-5 text-primary" />
                <DrawerTitle>Builder de Relatórios</DrawerTitle>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => exportData('excel')} disabled={!canExport} className="gap-1.5">
                  <FileDown className="h-3.5 w-3.5" /> Excel
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportData('csv')} disabled={!canExport} className="gap-1.5">
                  <FileDown className="h-3.5 w-3.5" /> CSV
                </Button>
                <Button size="sm" onClick={() => setSaveDialogOpen(true)} disabled={!canExport} className="gap-1.5">
                  <Save className="h-3.5 w-3.5" /> Salvar
                </Button>
              </div>
            </div>
          </DrawerHeader>

          <Tabs defaultValue="builder" className="flex flex-col flex-1 overflow-hidden">
            <TabsList className="mx-6 mt-4 mb-0 shrink-0 w-fit">
              <TabsTrigger value="builder" className="gap-1.5 text-xs">
                <Settings2 className="h-3.5 w-3.5" /> Configurar
              </TabsTrigger>
              <TabsTrigger value="preview" className="gap-1.5 text-xs">
                <Eye className="h-3.5 w-3.5" /> Pré-visualização
                {previewRows.length > 0 && (
                  <span className="ml-1 text-xs bg-primary/15 text-primary px-1.5 rounded-full font-medium">
                    {previewRows.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="templates" className="gap-1.5 text-xs">
                <BookOpen className="h-3.5 w-3.5" /> Salvos
                {templates.length > 0 && (
                  <span className="ml-1 text-xs bg-muted text-muted-foreground px-1.5 rounded-full">
                    {templates.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="schedule" className="gap-1.5 text-xs" disabled={!canExport}>
                <CalendarClock className="h-3.5 w-3.5" /> Agendar
              </TabsTrigger>
            </TabsList>

            {/* ── BUILDER TAB ──────────────────────────────────────── */}
            <TabsContent value="builder" className="flex-1 overflow-hidden mt-0">
              <ScrollArea className="h-full px-6 py-4">
                <div className="space-y-6 pb-6">

                  {/* Seletor de escopo */}
                  <PortfolioScopeSelector
                    escopo={escopo}
                    onEscopoChange={setEscopo}
                    clusterId={portfolioClusterId}
                    onClusterChange={setPortfolioClusterId}
                    contratoIds={portfolioContratoIds}
                    onContratoIdsChange={setPortfolioContratoIds}
                    contratoAncoraNome={contratoNome}
                    isGestorOrAbove={isGestorOrAbove}
                  />

                  <Separator />

                  {/* Dataset */}
                  <DatasetSelector
                    datasets={availableDatasets}
                    selected={dataset}
                    onChange={setDataset}
                  />

                  <Separator />

                  {/* Columns */}
                  <ColumnPicker
                    columns={currentDataset.columns}
                    selected={config.columns}
                    onToggle={toggleColumn}
                  />

                  {config.columns.length > 0 && (
                    <>
                      <Separator />
                      <FilterPanel
                        columns={currentDataset.columns}
                        filters={config.filters}
                        onAdd={addFilter}
                        onUpdate={updateFilter}
                        onRemove={removeFilter}
                      />
                      <Separator />
                      <SortPanel
                        columns={currentDataset.columns}
                        selected={config.columns}
                        sortBy={config.sortBy}
                        onToggle={toggleSort}
                      />
                    </>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* ── PREVIEW TAB ──────────────────────────────────────── */}
            <TabsContent value="preview" className="flex-1 overflow-hidden mt-0">
              <ScrollArea className="h-full px-6 py-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    Pré-visualização — {currentDataset.label}
                    {escopo === 'portfolio' && (
                      <span className="ml-2 text-primary">(portfólio: {portfolioContratoIds.length} contratos)</span>
                    )}
                  </p>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => schedulePreview(config, dataset)}
                    className="gap-1.5 h-7 text-xs"
                    disabled={previewLoading || !canExport}
                  >
                    <Play className="h-3 w-3" /> Atualizar
                  </Button>
                </div>
                <PreviewTable
                  columns={currentDataset.columns}
                  selectedCols={config.columns}
                  rows={previewRows}
                  loading={previewLoading}
                  error={previewError}
                />
              </ScrollArea>
            </TabsContent>

            {/* ── TEMPLATES TAB ──────────────────────────────────────── */}
            <TabsContent value="templates" className="flex-1 overflow-hidden mt-0">
              <ScrollArea className="h-full px-6 py-4">
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    Relatórios salvos
                  </p>
                  <TemplatesList
                    templates={templates}
                    loading={templatesLoading}
                    onLoad={(t) => { loadTemplate(t); setLoadedTemplateId(t.id); }}
                    onDelete={(id) => deleteTemplate.mutate(id)}
                    onToggleFav={(id, val) => toggleFavorito.mutate({ id, val })}
                  />
                </div>
              </ScrollArea>
            </TabsContent>

            {/* ── SCHEDULE TAB ──────────────────────────────────────── */}
            <TabsContent value="schedule" className="flex-1 overflow-hidden mt-0">
              <ScrollArea className="h-full px-6 py-4">
                <ScheduleTab contratoId={contratoId} templateId={loadedTemplateId} />
              </ScrollArea>
            </TabsContent>
          </Tabs>

          <DrawerFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={onClose} className="w-full">Fechar</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <SaveTemplateDialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        onSave={(payload) => saveTemplate.mutate(payload)}
        isSaving={saveTemplate.isPending}
      />
    </>
  );
};
